/**
 * A small UI kit built directly on the design tokens.
 *
 * Hand-rolled rather than pulled from shadcn wholesale: the application needs
 * roughly a dozen primitives, and keeping them in one file means the status
 * ramp, radius and typography stay consistent without auditing sixty generated
 * files. Radix is still used where real accessibility behaviour is needed
 * (dialogs, selects); these are the plain ones.
 */

import * as React from 'react';
import { createPortal } from 'react-dom';
import { cva, type VariantProps } from 'class-variance-authority';
import { ChevronLeft, ChevronRight, Loader2, X } from 'lucide-react';
import { cn } from '@/lib/utils';

// --- Button ---------------------------------------------------------------

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-semibold transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 active:scale-[0.98] [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-foreground shadow-brand hover:bg-primary/92',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        outline:
          'border border-input bg-background shadow-xs hover:border-primary/30 hover:bg-accent hover:text-accent-foreground',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        // Uses the danger token, never brand maroon — a destructive action must
        // not look like a branded one.
        danger: 'bg-danger text-danger-foreground shadow-card hover:bg-danger/90',
        success: 'bg-success text-success-foreground shadow-card hover:bg-success/90',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-9 px-3.5',
        md: 'h-10 px-4',
        lg: 'h-12 px-8 text-base',
        icon: 'h-10 w-10',
      },
      full: { true: 'w-full' },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, full, loading, children, disabled, ...props }, ref) => (
    <button
      ref={ref}
      className={cn(buttonVariants({ variant, size, full }), className)}
      disabled={disabled || loading}
      {...props}
    >
      {loading && <Loader2 className="animate-spin" aria-hidden="true" />}
      {children}
    </button>
  ),
);
Button.displayName = 'Button';

// --- Input ----------------------------------------------------------------

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, invalid, ...props }, ref) => (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(
        'flex h-11 w-full rounded-xl border border-input bg-background px-3.5 py-2 text-sm text-foreground shadow-xs transition-all placeholder:text-muted-foreground hover:border-primary/25 focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 disabled:cursor-not-allowed disabled:bg-muted disabled:opacity-60',
        invalid && 'border-danger focus-visible:border-danger focus-visible:ring-danger/20',
        className,
      )}
      {...props}
    />
  ),
);
Input.displayName = 'Input';

export const Textarea = React.forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }
>(({ className, invalid, ...props }, ref) => (
  <textarea
    ref={ref}
    aria-invalid={invalid || undefined}
    className={cn(
      'flex min-h-[88px] w-full rounded-xl border border-input bg-background px-3.5 py-2.5 text-sm text-foreground shadow-xs transition-all placeholder:text-muted-foreground hover:border-primary/25 focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 disabled:opacity-60',
      invalid && 'border-danger focus-visible:border-danger focus-visible:ring-danger/20',
      className,
    )}
    {...props}
  />
));
Textarea.displayName = 'Textarea';

export const Select = React.forwardRef<
  HTMLSelectElement,
  React.SelectHTMLAttributes<HTMLSelectElement> & { invalid?: boolean }
>(({ className, invalid, children, ...props }, ref) => (
  <select
    ref={ref}
    aria-invalid={invalid || undefined}
    className={cn(
      'flex h-11 w-full rounded-xl border border-input bg-background px-3.5 py-2 text-sm text-foreground shadow-xs transition-all hover:border-primary/25 focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 disabled:opacity-60',
      invalid && 'border-danger focus-visible:border-danger focus-visible:ring-danger/20',
      className,
    )}
    {...props}
  >
    {children}
  </select>
));
Select.displayName = 'Select';

// --- Field ----------------------------------------------------------------

interface FieldProps {
  label: string;
  htmlFor?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
  className?: string;
}

/**
 * Label + control + error, wired together.
 *
 * The error is rendered in `danger`, associated with the control via
 * `aria-describedby` by convention, and given `role="alert"` so a screen reader
 * announces a validation failure rather than leaving it purely visual.
 */
export function Field({
  label,
  htmlFor,
  error,
  hint,
  required,
  children,
  className,
}: FieldProps) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <label
        htmlFor={htmlFor}
        className="block text-sm font-semibold text-foreground"
      >
        {label}
        {required && <span className="ml-0.5 text-danger">*</span>}
      </label>
      {children}
      {hint && !error && <p className="text-xs text-foreground-secondary">{hint}</p>}
      {error && (
        <p role="alert" className="text-xs font-medium text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

// --- Card -----------------------------------------------------------------

export function Card({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('rounded-2xl border border-border bg-card text-card-foreground shadow-card', className)}
      {...props}
    />
  );
}

export function CardHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex flex-col space-y-1.5 p-6', className)} {...props} />;
}

export function CardTitle({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h3
      className={cn('font-heading text-lg font-bold leading-none tracking-tight', className)}
      {...props}
    />
  );
}

export function CardDescription({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn('text-sm text-foreground-secondary', className)} {...props} />;
}

export function CardContent({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('p-6 pt-0', className)} {...props} />;
}

export function CardFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex items-center p-6 pt-0', className)} {...props} />;
}

// --- Alert ----------------------------------------------------------------

const alertVariants = cva('rounded-xl border p-4 text-sm leading-relaxed', {
  variants: {
    tone: {
      info: 'border-info/20 bg-info-soft text-info-soft-foreground',
      success: 'border-success/20 bg-success-soft text-success-soft-foreground',
      warning: 'border-warning/20 bg-warning-soft text-warning-soft-foreground',
      danger: 'border-danger/20 bg-danger-soft text-danger-soft-foreground',
      neutral: 'border-border bg-muted text-foreground-secondary',
    },
  },
  defaultVariants: { tone: 'neutral' },
});

export function Alert({
  className,
  tone,
  title,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants> & { title?: string }) {
  return (
    <div role="alert" className={cn(alertVariants({ tone }), className)} {...props}>
      {title && <p className="mb-1 font-bold">{title}</p>}
      {children}
    </div>
  );
}

// --- Stat tile ------------------------------------------------------------

export function Stat({
  label,
  value,
  hint,
  tone = 'neutral',
  icon,
  className,
}: {
  label: string;
  value: React.ReactNode;
  hint?: string;
  tone?: 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'brand';
  icon?: React.ReactNode;
  className?: string;
}) {
  const valueTone = {
    neutral: 'text-foreground',
    success: 'text-success',
    warning: 'text-warning',
    danger: 'text-danger',
    info: 'text-info',
    brand: 'text-primary',
  }[tone];

  const iconTone = {
    neutral: 'bg-muted text-foreground-secondary',
    success: 'bg-success-soft text-success',
    warning: 'bg-warning-soft text-warning',
    danger: 'bg-danger-soft text-danger',
    info: 'bg-info-soft text-info',
    brand: 'bg-primary/10 text-primary',
  }[tone];

  return (
    <Card className={cn('p-5', className)}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-[11px] font-bold uppercase tracking-[0.12em] text-foreground-secondary">
            {label}
          </p>
          {/* Display weight and tabular figures: a balance should read as one
              object, and columns of naira must line up. */}
          <p className={cn('display-figure mt-2 text-[26px] leading-none', valueTone)}>{value}</p>
          {hint && <p className="mt-2 truncate text-xs text-foreground-secondary">{hint}</p>}
        </div>
        {icon && (
          <span
            className={cn(
              'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl',
              iconTone,
            )}
          >
            {icon}
          </span>
        )}
      </div>
    </Card>
  );
}

/** A labelled on/off control. Used throughout settings. */
export function Switch({
  checked,
  onChange,
  disabled,
  label,
  id,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
  label: string;
  id?: string;
}) {
  return (
    <button
      type="button"
      id={id}
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50',
        checked ? 'bg-success' : 'bg-muted-foreground/30',
      )}
    >
      <span
        className={cn(
          'inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform',
          checked ? 'translate-x-6' : 'translate-x-1',
        )}
      />
    </button>
  );
}

/**
 * Centred overlay panel.
 *
 * Closes on Escape and on a backdrop click, and locks page scroll while open —
 * the three things a hand-rolled modal usually forgets.
 */
export function Modal({
  open,
  onClose,
  children,
  size = 'md',
}: {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}) {
  React.useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);

  if (!open) return null;

  const widths = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-3xl',
  }[size];

  // Rendered into the body rather than where it is written. A modal opened
  // from inside a grid cell was widening that cell — a dialog has no business
  // taking part in the layout of the page behind it.
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center sm:p-6">
      <div
        className="absolute inset-0 bg-foreground/40 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        className={cn(
          'relative max-h-[92vh] w-full overflow-y-auto rounded-t-2xl border border-border bg-card shadow-overlay sm:rounded-2xl',
          widths,
        )}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}

export function ModalHeader({
  title,
  description,
  onClose,
}: {
  title: string;
  description?: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-border p-6">
      <div className="min-w-0">
        <h2 className="font-heading text-lg font-bold text-card-foreground">{title}</h2>
        {description && <p className="mt-1 text-sm text-foreground-secondary">{description}</p>}
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="shrink-0 rounded-lg p-1.5 text-foreground-secondary transition-colors hover:bg-accent hover:text-foreground"
      >
        <X size={18} />
      </button>
    </div>
  );
}

// --- Table ----------------------------------------------------------------

export function Table({ className, ...props }: React.HTMLAttributes<HTMLTableElement>) {
  return (
    <div className="w-full overflow-x-auto">
      <table className={cn('w-full caption-bottom text-sm', className)} {...props} />
    </div>
  );
}

export function Th({ className, numeric, ...props }: React.ThHTMLAttributes<HTMLTableCellElement> & { numeric?: boolean }) {
  return (
    <th
      className={cn(
        'border-b border-border px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-foreground-secondary',
        numeric && 'numeric text-right',
        className,
      )}
      {...props}
    />
  );
}

export function Td({ className, numeric, ...props }: React.TdHTMLAttributes<HTMLTableCellElement> & { numeric?: boolean }) {
  return (
    <td
      className={cn('border-b border-border px-4 py-3 text-foreground', numeric && 'numeric text-right', className)}
      {...props}
    />
  );
}

// --- Misc -----------------------------------------------------------------

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded-md bg-muted', className)} />;
}

export interface PageMeta {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

/**
 * Page an array the API already sent in full.
 *
 * The member's own lists are short enough to fetch in one request, so paging
 * them over the network would cost a round trip to save nothing. This gives
 * them the same pager as the admin lists without the server involved.
 */
export function usePagedList<T>(items: T[], pageSize: number) {
  const [page, setPage] = React.useState(1);
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const current = Math.min(page, totalPages);

  React.useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  return {
    rows: items.slice((current - 1) * pageSize, current * pageSize),
    setPage,
    meta: {
      page: current,
      pageSize,
      total: items.length,
      totalPages,
      hasNextPage: current < totalPages,
      hasPreviousPage: current > 1,
    } satisfies PageMeta,
  };
}

/**
 * Pager for the admin lists.
 *
 * Shows the range rather than only page numbers: "showing 26-50 of 338" is what
 * someone reconciling a ledger actually needs to know, and a page number alone
 * hides how much is left.
 */
export function Pagination({
  meta,
  onPageChange,
  className,
}: {
  meta: PageMeta | undefined;
  onPageChange: (page: number) => void;
  className?: string;
}) {
  if (!meta || meta.total === 0) return null;

  const first = (meta.page - 1) * meta.pageSize + 1;
  const last = Math.min(meta.page * meta.pageSize, meta.total);

  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-3 border-t border-border px-5 py-3.5',
        className,
      )}
    >
      <p className="text-sm text-foreground-secondary">
        Showing <span className="numeric font-semibold text-foreground">{first}</span>&ndash;
        <span className="numeric font-semibold text-foreground">{last}</span> of{' '}
        <span className="numeric font-semibold text-foreground">{meta.total}</span>
      </p>

      {meta.totalPages > 1 && (
        <div className="flex items-center gap-1.5">
          <Button
            size="sm"
            variant="outline"
            disabled={!meta.hasPreviousPage}
            onClick={() => onPageChange(meta.page - 1)}
          >
            <ChevronLeft size={15} /> Previous
          </Button>

          <div className="hidden items-center gap-1 sm:flex">
            {pageNumbers(meta.page, meta.totalPages).map((entry, index) =>
              entry === null ? (
                <span key={`gap-${index}`} className="px-1 text-foreground-secondary">
                  &hellip;
                </span>
              ) : (
                <button
                  key={entry}
                  type="button"
                  onClick={() => onPageChange(entry)}
                  aria-current={entry === meta.page ? 'page' : undefined}
                  className={cn(
                    'numeric h-8 min-w-8 rounded-lg px-2 text-sm font-semibold transition-colors',
                    entry === meta.page
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground-secondary hover:bg-accent hover:text-accent-foreground',
                  )}
                >
                  {entry}
                </button>
              ),
            )}
          </div>

          <Button
            size="sm"
            variant="outline"
            disabled={!meta.hasNextPage}
            onClick={() => onPageChange(meta.page + 1)}
          >
            Next <ChevronRight size={15} />
          </Button>
        </div>
      )}
    </div>
  );
}

/** First, last, and a window around the current page; `null` marks an ellipsis. */
function pageNumbers(current: number, total: number): Array<number | null> {
  if (total <= 7) return Array.from({ length: total }, (_, index) => index + 1);

  const pages = new Set([1, total, current, current - 1, current + 1]);
  const sorted = [...pages].filter((page) => page >= 1 && page <= total).sort((a, b) => a - b);

  const output: Array<number | null> = [];
  let previous = 0;
  for (const page of sorted) {
    if (previous && page - previous > 1) output.push(null);
    output.push(page);
    previous = page;
  }
  return output;
}

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: React.ReactNode;
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border px-6 py-16 text-center">
      {icon && (
        <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-muted text-foreground-secondary">
          {icon}
        </span>
      )}
      <h3 className="font-heading text-lg font-bold text-foreground">{title}</h3>
      {description && (
        <p className="mt-1 max-w-sm text-sm text-foreground-secondary">{description}</p>
      )}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

export function PageHeader({
  eyebrow,
  title,
  description,
  status,
  actions,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  /**
   * A badge belonging to the thing named in the title. It sits on the title's
   * own line at every width — dropped below on a phone, as `actions` are, it
   * reads as a stray chip with nothing to attach itself to.
   */
  status?: React.ReactNode;
  actions?: React.ReactNode;
}) {
  return (
    <div className="mb-8 flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
      <div className="min-w-0 flex-1">
        {eyebrow && <p className="eyebrow mb-2">{eyebrow}</p>}
        <div className="flex items-center justify-between gap-3">
          <h1 className="font-heading text-[28px] font-bold leading-tight tracking-tight text-foreground md:text-[32px]">
            {title}
          </h1>
          {status && <span className="shrink-0">{status}</span>}
        </div>
        {description && (
          <p className="mt-1.5 max-w-2xl text-[15px] leading-relaxed text-foreground-secondary">
            {description}
          </p>
        )}
      </div>
      {actions && <div className="flex shrink-0 flex-wrap gap-2">{actions}</div>}
    </div>
  );
}
